/**
 * usePhotoViewer - Shared hook for Edit and View pages
 *
 * Extracts common state management from Edit.jsx and View.jsx:
 * - Photo list loading and filtering
 * - Selected photo management
 * - Display preferences (viz/edit toggle, crop mask)
 * - Image bounds for overlays
 * - Navigation helpers
 *
 * Edit-only features NOT included here:
 * - Crop mode (useCropEditor)
 * - Face selection/hover (useFaceManagement)
 * - wasm-vips processing
 * - Person modal (usePersonForm)
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLocalStorage, useLocalStorageBool } from '../../../hooks/useLocalStorage';
import { usePermissions } from '../../../hooks';
import {
  fetchAllPhotos,
  fetchPhoto,
  fetchFacesByImage,
  fetchPhotoMeta,
} from '../../../api';
import { resolveUrl, CENTRAL_BASE, toList } from '../../../utils/url';
import { computeCropBox } from '../../../utils/cropCalculations';
import {
  STORAGE_KEYS,
  mapFiltersToApiStatus,
  photoMatchesFilters,
} from '../../../utils/constants';

/**
 * Shared photo viewer hook for Edit and View pages.
 *
 * @param {Object} options Configuration options
 * @param {boolean} [options.autoSelectFirst=true] Auto-select first photo when list loads
 * @param {string} [options.initialPhotoId] Initial photo ID from URL hash or storage
 * @param {string} [options.storagePrefix='matrix'] Prefix for localStorage keys
 * @param {string[]} [options.defaultStatuses] Default status filter (Edit: editable, View: all)
 * @param {number} [options.pageSize=400] Number of photos to fetch per page
 * @returns {Object} Photo viewer state and handlers
 */
export default function usePhotoViewer({
  autoSelectFirst = true,
  initialPhotoId = null,
  storagePrefix = 'matrix',
  defaultStatuses = null,
  pageSize = 400,
} = {}) {
  // Permission hooks
  const { isPhotoReadOnly, canEditPhotos, canDeletePhotos, canApprovePhotos } = usePermissions();

  // Photo list state
  const [photos, setPhotos] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState(null);

  // Selected photo state
  const [selectedPhotoId, setSelectedPhotoId] = useState(initialPhotoId);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [metaDoc, setMetaDoc] = useState(null);
  const [faces, setFaces] = useState([]);
  const [loadingPhoto, setLoadingPhoto] = useState(false);

  // Display preferences with localStorage persistence
  const [showVizImage, setShowVizImage] = useLocalStorageBool(
    `${storagePrefix}-show-viz`,
    true
  );
  const [showCropMask, setShowCropMask] = useLocalStorageBool(
    `${storagePrefix}-crop-mask`,
    true
  );

  // Filters and search
  const [filters, setFilters] = useLocalStorage(
    `${storagePrefix}-filters`,
    []
  );
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useLocalStorage(
    STORAGE_KEYS.SORT_ORDER,
    'oldest'
  );

  // Image bounds for overlay positioning
  const [imgBounds, setImgBounds] = useState(null);
  const [vizNaturalDims, setVizNaturalDims] = useState(null);
  const imgRef = useRef(null);
  const imgWrapperRef = useRef(null);
  const actualImgRef = useRef(null);

  // Refs for stable callbacks
  const selectedPhotoIdRef = useRef(selectedPhotoId);
  useEffect(() => {
    selectedPhotoIdRef.current = selectedPhotoId;
  }, [selectedPhotoId]);

  // Compute read-only mode based on permissions and photo status
  const isReadOnly = useMemo(() => {
    return isPhotoReadOnly(selectedPhoto);
  }, [selectedPhoto, isPhotoReadOnly]);

  // Map sort order to API field
  const mapSortToApi = useCallback((sort) => {
    const orderingMap = {
      oldest: 'created_at',
      newest: '-created_at',
      filename_asc: 'filename',
      filename_desc: '-filename',
      capture_oldest: 'capture_date',
      capture_newest: '-capture_date',
    };
    return orderingMap[sort] || 'created_at';
  }, []);

  // Load photos list
  const loadPhotos = useCallback(async (options = {}) => {
    const {
      keepSelection = true,
      activeFilters = filters,
      activeSearch = search,
    } = options;

    try {
      setLoadingList(true);
      setError(null);

      const params = {
        limit: pageSize,
        offset: 0,
        ordering: mapSortToApi(sortOrder),
      };

      if (activeSearch) params.q = activeSearch;

      // Apply status filters
      const active = activeFilters || [];
      if (active.length > 0 && !active.includes('all')) {
        const statusFilters = mapFiltersToApiStatus(active);
        if (statusFilters.length) {
          params.status = statusFilters.join(',');
        }
      } else if (defaultStatuses) {
        // Apply default statuses if provided (e.g., Edit page = editable statuses only)
        params.status = defaultStatuses.join(',');
      }

      const data = await fetchAllPhotos(params.limit, params.offset, params);
      const photoList = toList(data);
      setPhotos(photoList);

      // Auto-select first photo if requested and no selection
      if (autoSelectFirst && !selectedPhotoIdRef.current && photoList.length > 0) {
        setSelectedPhotoId(photoList[0].image_id);
      }

      // Restore selection if it exists in new list
      if (keepSelection && selectedPhotoIdRef.current) {
        const exists = photoList.some(p => p.image_id === selectedPhotoIdRef.current);
        if (!exists && photoList.length > 0) {
          setSelectedPhotoId(photoList[0].image_id);
        }
      }

      return photoList;
    } catch (err) {
      console.error('Failed to load photos:', err);
      setError(err.message || 'Failed to load photos');
      return [];
    } finally {
      setLoadingList(false);
    }
  }, [filters, search, sortOrder, pageSize, autoSelectFirst, defaultStatuses, mapSortToApi]);

  // Load selected photo details
  const loadSelectedPhoto = useCallback(async (photoId) => {
    if (!photoId) {
      setSelectedPhoto(null);
      setMetaDoc(null);
      setFaces([]);
      return;
    }

    try {
      setLoadingPhoto(true);
      setMetaDoc(null); // Reset metadata to prevent stale data
      setVizNaturalDims(null); // Reset viz dimensions

      const [photoData, facesData, metaData] = await Promise.all([
        fetchPhoto(photoId),
        fetchFacesByImage(photoId),
        fetchPhotoMeta(photoId),
      ]);

      // Guard against race condition: only update state if this photoId
      // is still the currently selected one (user may have clicked another photo)
      if (selectedPhotoIdRef.current !== photoId) {
        return null; // Stale request, discard results
      }

      setSelectedPhoto(photoData);
      setFaces(toList(facesData));
      setMetaDoc(metaData);

      return { photo: photoData, faces: toList(facesData), meta: metaData };
    } catch (err) {
      console.error('Failed to load photo:', err);
      return null;
    } finally {
      // Only clear loading if this is still the current photo
      if (selectedPhotoIdRef.current === photoId) {
        setLoadingPhoto(false);
      }
    }
  }, []);

  // Load selected photo when ID changes
  useEffect(() => {
    loadSelectedPhoto(selectedPhotoId);
  }, [selectedPhotoId, loadSelectedPhoto]);

  // Parse URL hash on mount for initial photo and filters
  // Format: #image=UUID&filter=tagging,review
  useEffect(() => {
    const hash = window.location.hash || '';
    const imageMatch = hash.match(/image=([^&]+)/);
    const filterMatch = hash.match(/filter=([^&]+)/);

    if (imageMatch?.[1]) {
      const imgId = decodeURIComponent(imageMatch[1]);
      setSelectedPhotoId(imgId);
      selectedPhotoIdRef.current = imgId;
    }

    if (filterMatch?.[1]) {
      const urlFilters = decodeURIComponent(filterMatch[1]).split(',').filter(Boolean);
      if (urlFilters.length > 0) {
        setFilters(urlFilters);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial load on mount
  useEffect(() => {
    loadPhotos();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Computed crop box from metadata
  const cropBox = useMemo(
    () => computeCropBox(metaDoc, selectedPhoto),
    [metaDoc, selectedPhoto]
  );

  // URL for viz mode base image (uncropped preview - VizOverlay renders overlays client-side)
  const currentVizUrl = useMemo(() => {
    // Use uncropped preview as base - VizOverlay Canvas draws detection boxes on top
    if (selectedPhoto?.preview_url) return selectedPhoto.preview_url;
    if (!selectedPhoto?.filename || !selectedPhotoId) return null;
    const stem = selectedPhoto.filename.replace(/\.[^/.]+$/, '');
    return `${CENTRAL_BASE.replace(/\/$/, '')}/${selectedPhotoId}/${stem}__preview.jpg`;
  }, [selectedPhoto, selectedPhotoId]);

  // URL for edited (cropped) image
  const currentEditedUrl = useMemo(() => {
    if (selectedPhoto?.edited_preview_url) return selectedPhoto.edited_preview_url;
    if (selectedPhoto?.edited_url) return selectedPhoto.edited_url;
    if (selectedPhoto?.preview_url) return selectedPhoto.preview_url;
    return null;
  }, [selectedPhoto]);

  // Full resolution URL
  const fullResUrl = useMemo(() => {
    if (!selectedPhoto) return null;
    return resolveUrl(selectedPhoto.edited_url || selectedPhoto.preview_url);
  }, [selectedPhoto]);

  // Filter photos client-side for immediate UI updates
  const filteredPhotos = useMemo(() => {
    let result = [...photos];

    // Client-side status filtering (for tagging/review split from pending)
    if (filters.length > 0 && !filters.includes('all')) {
      result = result.filter(p => photoMatchesFilters(p, filters));
    }

    // Client-side search filter
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(p => {
        const filename = p.filename || '';
        const imageId = p.image_id || '';
        return filename.toLowerCase().includes(term) || imageId.toLowerCase().includes(term);
      });
    }

    return result;
  }, [photos, search, filters]);

  // Navigation helpers
  const getNextPhotoId = useCallback(() => {
    const idx = filteredPhotos.findIndex(p => p.image_id === selectedPhotoId);
    if (idx === -1 || idx >= filteredPhotos.length - 1) return null;
    return filteredPhotos[idx + 1].image_id;
  }, [filteredPhotos, selectedPhotoId]);

  const getPrevPhotoId = useCallback(() => {
    const idx = filteredPhotos.findIndex(p => p.image_id === selectedPhotoId);
    if (idx <= 0) return null;
    return filteredPhotos[idx - 1].image_id;
  }, [filteredPhotos, selectedPhotoId]);

  const selectNextPhoto = useCallback(() => {
    const nextId = getNextPhotoId();
    if (nextId) setSelectedPhotoId(nextId);
    return nextId;
  }, [getNextPhotoId]);

  const selectPrevPhoto = useCallback(() => {
    const prevId = getPrevPhotoId();
    if (prevId) setSelectedPhotoId(prevId);
    return prevId;
  }, [getPrevPhotoId]);

  // Calculate actual image bounds within the container (accounts for object-fit: contain)
  // This matches Edit.jsx's letterbox calculation for accurate face overlay positioning
  const updateImgBounds = useCallback(() => {
    const container = imgWrapperRef.current;
    if (!container) {
      setImgBounds(null);
      return null;
    }

    const img = actualImgRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) {
      setImgBounds(null);
      return null;
    }

    const nW = img.naturalWidth;
    const nH = img.naturalHeight;

    const containerRect = container.getBoundingClientRect();
    const cW = containerRect.width;
    const cH = containerRect.height;

    // With object-fit: contain, calculate the actual rendered size and position
    // The element is scaled to fit within the container while maintaining aspect ratio
    const containerAspect = cW / cH;
    const imageAspect = nW / nH;

    let renderedW, renderedH, offsetX, offsetY;

    if (imageAspect > containerAspect) {
      // Image is wider than container - width constrained, letterboxed top/bottom
      renderedW = cW;
      renderedH = cW / imageAspect;
      offsetX = 0;
      offsetY = (cH - renderedH) / 2;
    } else {
      // Image is taller than container - height constrained, letterboxed left/right
      renderedH = cH;
      renderedW = cH * imageAspect;
      offsetX = (cW - renderedW) / 2;
      offsetY = 0;
    }

    // Return bounds with letterbox offsets (x, y) and rendered dimensions (w, h)
    const bounds = { x: offsetX, y: offsetY, w: renderedW, h: renderedH, nW, nH };
    setImgBounds(bounds);
    return bounds;
  }, []);

  // Update bounds on image load and container resize
  useEffect(() => {
    const img = actualImgRef.current;
    const container = imgWrapperRef.current;

    if (img) {
      img.addEventListener('load', updateImgBounds);
      // Also update if already loaded
      if (img.complete) updateImgBounds();
    }

    // ResizeObserver for container size changes
    let resizeObserver = null;
    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateImgBounds);
      resizeObserver.observe(container);
    }

    return () => {
      if (img) img.removeEventListener('load', updateImgBounds);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [updateImgBounds, selectedPhoto, showVizImage]);

  // Recalculate bounds when viz/edit toggle changes (image source changes)
  useEffect(() => {
    // Small delay to let the new image render
    const timer = setTimeout(updateImgBounds, 50);
    return () => clearTimeout(timer);
  }, [showVizImage, updateImgBounds]);

  // Toggle handlers for display preferences
  const toggleVizImage = useCallback(() => {
    setShowVizImage(v => !v);
  }, [setShowVizImage]);

  const toggleCropMask = useCallback(() => {
    setShowCropMask(v => !v);
  }, [setShowCropMask]);

  // Filter toggle handler
  const toggleFilter = useCallback((key) => {
    setFilters(prev => {
      if (key === 'all') {
        return [];
      }
      const newFilters = prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev.filter(k => k !== 'all'), key];
      return newFilters;
    });
  }, [setFilters]);

  // Reload photos when filters or sort changes
  useEffect(() => {
    if (!loadingList) {
      loadPhotos({ keepSelection: true });
    }
  }, [filters, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tagged persons from faces (for display)
  const taggedPersons = useMemo(() => {
    const nameMap = {};
    (selectedPhoto?.persons || []).forEach(p => {
      if (p.id && p.display_name) {
        nameMap[p.id] = p.display_name;
      }
    });

    const seenPersonIds = new Set();
    const fromFaces = faces
      .filter(f => f.person && !f.ignored)
      .map(f => {
        seenPersonIds.add(f.person);
        return {
          id: f.id,
          person_id: f.person,
          display_name: nameMap[f.person] || f.person_name || f.person_display || null,
          preview_url: f.preview_url,
          bbox: f.bbox,
          crop_width: f.crop_width,
          crop_height: f.crop_height,
        };
      });

    const manualEntries = (selectedPhoto?.persons || [])
      .filter(p => !seenPersonIds.has(p.id))
      .map(p => ({
        id: `manual-${p.id}`,
        person_id: p.id,
        display_name: p.display_name,
        first_name: p.first_name,
        last_name: p.last_name,
        jersey_number: p.jersey_number,
        face_url: null,
      }));

    return [...fromFaces, ...manualEntries];
  }, [faces, selectedPhoto]);

  // Return all state and handlers
  return {
    // Photo list
    photos,
    filteredPhotos,
    loadingList,
    error,
    loadPhotos,

    // Selected photo
    selectedPhotoId,
    selectedPhoto,
    metaDoc,
    faces,
    setFaces,
    loadingPhoto,
    setSelectedPhotoId,
    loadSelectedPhoto,

    // Display preferences
    showVizImage,
    setShowVizImage,
    toggleVizImage,
    showCropMask,
    setShowCropMask,
    toggleCropMask,

    // Image bounds
    imgBounds,
    setImgBounds,
    vizNaturalDims,
    setVizNaturalDims,
    imgRef,
    imgWrapperRef,
    actualImgRef,
    updateImgBounds,

    // Filters and search
    filters,
    setFilters,
    toggleFilter,
    search,
    setSearch,
    sortOrder,
    setSortOrder,

    // Computed values
    cropBox,
    currentVizUrl,
    currentEditedUrl,
    fullResUrl,
    taggedPersons,
    isReadOnly,

    // Permissions
    canEditPhotos,
    canDeletePhotos,
    canApprovePhotos,
    isPhotoReadOnly,

    // Navigation
    getNextPhotoId,
    getPrevPhotoId,
    selectNextPhoto,
    selectPrevPhoto,
  };
}
